import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { type ITheme } from './index';
import { useMemo, type PropsWithChildren, type JSX } from 'react';
import { themeDeepMerge } from './utils/themeDeepMerge';
import { getLightTheme } from './themes/light';
import { getDarkTheme } from './themes/dark';
import { paletteTokens } from './generated/paletteVariables';
import { useBranding } from './brandingContext';

export type ThemeNames = 'light' | 'dark';

export type ThemeProviderProps = {
  themeName: ThemeNames;
};

const themeGetters = {
  light: getLightTheme,
  dark: getDarkTheme,
};

export const ThemeProvider = ({
  children,
  themeName,
}: PropsWithChildren<ThemeProviderProps>): JSX.Element => {
  const branding = useBranding();

  const activeTheme = useMemo(() => {
    const overridePalette = branding?.overridePalette;
    const overrideTheme = branding?.overrideTheme;

    const palette = overridePalette
      ? (themeDeepMerge(paletteTokens, overridePalette) as typeof paletteTokens)
      : undefined;

    const baseTheme = themeGetters[themeName](palette);

    if (!overrideTheme) {
      return baseTheme;
    }

    return themeDeepMerge(
      baseTheme as Record<keyof ITheme, unknown>,
      overrideTheme,
    ) as ITheme;
  }, [themeName, branding]);

  return <MuiThemeProvider theme={activeTheme}>{children}</MuiThemeProvider>;
};
